import { Head, Link } from '@inertiajs/react'
import AdminLayout from '../../components/layout/AdminLayout'

export default function PropertiesShow({ property, matches }) {
  return (
    <AdminLayout>
      <Head title={property.title || 'Propriété'} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <Link href="/properties" className="text-sm text-blue-600 hover:text-blue-800 mb-2 inline-block">
              ← Retour aux propriétés
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">{property.title || property.address}</h1>
            <p className="mt-2 text-sm text-gray-600">
              {property.city} {property.zipCode}
            </p>
          </div>

          <div className="flex gap-3">
            <Link
              href={`/properties/${property.id}/edit`}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Modifier
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Details Card */}
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Détails de la propriété</h2>

              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Type de bien</dt>
                  <dd className="mt-1 text-sm text-gray-900">{translatePropertyType(property.propertyType)}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Transaction</dt>
                  <dd className="mt-1 text-sm text-gray-900">{translateTransactionType(property.transactionType)}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Prix</dt>
                  <dd className="mt-1 text-lg font-semibold text-gray-900">{formatPrice(property.price)}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Statut</dt>
                  <dd className="mt-1">
                    <StatusBadge status={property.status} />
                  </dd>
                </div>

                {property.surface && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Surface</dt>
                    <dd className="mt-1 text-sm text-gray-900">{property.surface} m²</dd>
                  </div>
                )}

                {property.rooms && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Pièces</dt>
                    <dd className="mt-1 text-sm text-gray-900">{property.rooms}</dd>
                  </div>
                )}

                {property.bedrooms && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Chambres</dt>
                    <dd className="mt-1 text-sm text-gray-900">{property.bedrooms}</dd>
                  </div>
                )}

                {property.floor !== null && property.floor !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Étage</dt>
                    <dd className="mt-1 text-sm text-gray-900">{property.floor}</dd>
                  </div>
                )}
              </dl>

              {/* Équipements */}
              {(property.elevator || property.balcony || property.parking || property.garden) && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Équipements</h3>
                  <div className="flex flex-wrap gap-2">
                    {property.elevator && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        Ascenseur
                      </span>
                    )}
                    {property.balcony && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        Balcon
                      </span>
                    )}
                    {property.parking && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        Parking
                      </span>
                    )}
                    {property.garden && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        Jardin
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {property.description && (
              <div className="bg-white shadow-sm rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{property.description}</p>
              </div>
            )}

            {/* Address */}
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Adresse</h2>
              <address className="not-italic text-gray-700">
                {property.address}<br />
                {property.zipCode} {property.city}
              </address>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Réseau</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Disponible pour le réseau</span>
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    property.availableForNetwork
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {property.availableForNetwork ? 'Oui' : 'Non'}
                  </span>
                </div>
              </div>
            </div>

            {/* Matches */}
            {matches && matches.length > 0 && (
              <div className="bg-white shadow-sm rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Matches récents ({matches.length})
                </h2>
                <div className="space-y-3">
                  {matches.map((match) => (
                    <Link
                      key={match.id}
                      href={`/matches/${match.id}`}
                      className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {match.leadName || 'Lead'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Score: {match.relevanceScore}%
                      </div>
                    </Link>
                  ))}
                </div>
                <Link
                  href={`/matches?property_id=${property.id}`}
                  className="mt-4 block text-center text-sm text-blue-600 hover:text-blue-800"
                >
                  Voir tous les matches →
                </Link>
              </div>
            )}

            {/* Meta */}
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs text-gray-500">Créé le</dt>
                  <dd className="text-sm text-gray-900">{formatDate(property.createdAt)}</dd>
                </div>
                {property.updatedAt && (
                  <div>
                    <dt className="text-xs text-gray-500">Modifié le</dt>
                    <dd className="text-sm text-gray-900">{formatDate(property.updatedAt)}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

function translatePropertyType(type) {
  const translations = {
    'apartment': 'Appartement',
    'house': 'Maison',
    'land': 'Terrain',
    'commercial': 'Commercial',
    'parking': 'Parking'
  }
  return translations[type] || type
}

function translateTransactionType(type) {
  const translations = {
    'sale': 'Vente',
    'rental': 'Location'
  }
  return translations[type] || type
}

function StatusBadge({ status }) {
  const statusConfig = {
    available: { label: 'Disponible', color: 'bg-green-100 text-green-800' },
    under_offer: { label: 'Sous offre', color: 'bg-yellow-100 text-yellow-800' },
    sold: { label: 'Vendu', color: 'bg-blue-100 text-blue-800' },
    rented: { label: 'Loué', color: 'bg-purple-100 text-purple-800' },
    withdrawn: { label: 'Retiré', color: 'bg-red-100 text-red-800' }
  }

  const config = statusConfig[status] || statusConfig.available

  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  )
}

function formatPrice(price) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0
  }).format(price)
}

function formatDate(date) {
  return new Date(date).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}
